import { z } from 'zod';

export const ShiftTypeSchema = z.enum(['WORK', 'PTO']);
export type ShiftType = z.infer<typeof ShiftTypeSchema>;

export const listShiftsQuery = z.object({
  startDate: z.iso.datetime({ offset: true }).optional(),
  endDate: z.iso.datetime({ offset: true }).optional(),
});
export type ListShiftsQuery = z.infer<typeof listShiftsQuery>;

export const createShiftBody = z.object({
  userId: z.string().min(1),
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  repeats: z.boolean().optional().default(false),
  freq: z.number().int().optional(),
  interval: z.number().int().min(1).optional(),
  byDay: z.array(z.enum(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'])).optional().default([]),
  until: z.iso.datetime({ offset: true }).optional(),
}).refine(
  (v) => !v.repeats || !!v.until,
  { message: 'End date is required for recurring shifts.', path: ['until'] },
).refine(
  (v) => !v.repeats || v.freq === 2 || v.freq === 3,
  { message: 'Frequency must be DAILY (3) or WEEKLY (2).', path: ['freq'] },
).refine(
  (v) => v.freq !== 2 || (v.byDay?.length ?? 0) > 0,
  { message: 'At least one weekday must be selected for weekly recurrence.', path: ['byDay'] },
);
export type CreateShiftBody = z.infer<typeof createShiftBody>;

export const shiftOccurrenceParam = z.object({
  occurrenceId: z.uuid(),
});
export type ShiftOccurrenceParam = z.infer<typeof shiftOccurrenceParam>;

export const updateShiftBody = z.object({
  userId: z.string().min(1),
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  scope: z.enum(['this', 'all-future']).optional().default('this'),
});
export type UpdateShiftBody = z.infer<typeof updateShiftBody>;

export const deleteShiftQuery = z.object({
  scope: z.enum(['this', 'following']).optional().default('this'),
});
export type DeleteShiftQuery = z.infer<typeof deleteShiftQuery>;

export const shiftSummaryQuery = z.object({
  startDate: z.iso.datetime({ offset: true }).optional(),
  endDate: z.iso.datetime({ offset: true }).optional(),
});
export type ShiftSummaryQuery = z.infer<typeof shiftSummaryQuery>;

export const shiftSummaryItem = z.object({
  userId: z.string(),
  totalMinutes: z.number().int().nonnegative(),
});
export type ShiftSummaryItem = z.infer<typeof shiftSummaryItem>;

export const shiftSummaryResponse = z.array(shiftSummaryItem);
export type ShiftSummaryResponse = z.infer<typeof shiftSummaryResponse>;
